import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { Card, KpiCard } from "../../components/common/Card";
import { Badge } from "../../components/common/Badge";
import { DataTable, Column } from "../../components/common/Table";
import { Modal } from "../../components/common/Modal";
import type { PmSchedule, Plant, Unit, PmChecklistItem, Asset } from "../../types";

interface Compliance {
  totalDue: number;
  completedOnTime: number;
  compliancePercent: number;
}

const FREQUENCIES = ["DAILY", "WEEKLY", "MONTHLY", "QUARTERLY", "SEMI_ANNUAL", "ANNUAL", "RUNNING_HOUR", "PRODUCTION_COUNT"];

export default function PMScheduler() {
  const [schedules, setSchedules] = useState<PmSchedule[]>([]);
  const [compliance, setCompliance] = useState<Compliance | null>(null);
  const [plants, setPlants] = useState<Plant[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<PmSchedule | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c, p, u] = await Promise.all([
        api.get<PmSchedule[]>("/api/pm-schedules"),
        api.get<Compliance>("/api/pm-schedules/compliance"),
        api.get<Plant[]>("/api/master-data/plants"),
        api.get<Unit[]>("/api/master-data/units"),
      ]);
      setSchedules(s);
      setCompliance(c);
      setPlants(p);
      setUnits(u);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load PM schedules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const generateDue = async () => {
    await api.post("/api/pm-schedules/generate-due");
    load();
  };

  const columns: Column<PmSchedule>[] = [
    { header: "PM Code", accessor: (p) => <span className="font-mono text-xs">{p.pm_code}</span> },
    { header: "PM Name", accessor: (p) => p.pm_name },
    { header: "Plant", accessor: (p) => p.plant_name ?? "-" },
    { header: "Unit", accessor: (p) => p.unit_name ?? "-" },
    { header: "Frequency", accessor: (p) => `${p.frequency_type} (every ${p.frequency_interval})` },
    { header: "Next Due", accessor: (p) => p.next_due_date ?? "-" },
    { header: "Technician", accessor: (p) => p.technician_name ?? "Unassigned" },
    { header: "Active", accessor: (p) => (p.is_active ? <Badge value="RUNNING" label="Active" /> : <Badge value="IDLE" label="Inactive" />) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">PM Scheduler</h1>
          <p className="text-sm text-slate-500">Daily / weekly / monthly / running-hour preventive maintenance plans. Click a row to edit.</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={generateDue}>
            Generate Due PM Work Orders
          </button>
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New PM Schedule
          </button>
        </div>
      </div>

      {compliance && (
        <div className="grid grid-cols-3 gap-4">
          <KpiCard label="Total PM Due" value={compliance.totalDue} accent="blue" />
          <KpiCard label="Completed On Time" value={compliance.completedOnTime} accent="green" />
          <KpiCard label="PM Compliance %" value={`${compliance.compliancePercent}%`} accent="amber" />
        </div>
      )}

      <Card>
        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : (
          <DataTable columns={columns} rows={schedules} keyField={(p) => p.id} onRowClick={setEditing} />
        )}
      </Card>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New PM Schedule">
        <PMScheduleForm
          plants={plants}
          units={units}
          onCreated={() => {
            setShowCreate(false);
            load();
          }}
        />
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.pm_code ?? ""} xl>
        {editing && (
          <PMScheduleEditor
            pm={editing}
            onChanged={() => {
              load();
              setEditing(null);
            }}
          />
        )}
      </Modal>
    </div>
  );
}

function PMScheduleForm({ plants, units, onCreated }: { plants: Plant[]; units: Unit[]; onCreated: () => void }) {
  const [form, setForm] = useState({ plantId: "", unitId: "", pmName: "", frequencyType: "MONTHLY", frequencyInterval: "1", nextDueDate: "" });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const unitsForPlant = useMemo(() => (form.plantId ? units.filter((u) => u.plant_id === Number(form.plantId)) : []), [units, form.plantId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.unitId) {
      setError("Plant and Unit are required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/api/pm-schedules", {
        unitId: Number(form.unitId),
        pmName: form.pmName,
        frequencyType: form.frequencyType,
        frequencyInterval: Number(form.frequencyInterval),
        nextDueDate: form.nextDueDate || undefined,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create PM schedule");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="grid grid-cols-2 gap-3">
        <select className="input-field" required value={form.plantId} onChange={(e) => setForm({ ...form, plantId: e.target.value, unitId: "" })}>
          <option value="">Select Plant</option>
          {plants.map((p) => (
            <option key={p.id} value={p.id}>
              {p.plant_name}
            </option>
          ))}
        </select>
        <select className="input-field" required disabled={!form.plantId} value={form.unitId} onChange={(e) => setForm({ ...form, unitId: e.target.value })}>
          <option value="">Select Unit</option>
          {unitsForPlant.map((u) => (
            <option key={u.id} value={u.id}>
              {u.unit_code} - {u.unit_name}
            </option>
          ))}
        </select>
      </div>
      <input className="input-field" placeholder="PM Name" required value={form.pmName} onChange={(e) => setForm({ ...form, pmName: e.target.value })} />
      <select className="input-field" value={form.frequencyType} onChange={(e) => setForm({ ...form, frequencyType: e.target.value })}>
        {FREQUENCIES.map((f) => (
          <option key={f} value={f}>
            {f.replace(/_/g, " ")}
          </option>
        ))}
      </select>
      <input className="input-field" type="number" min={1} placeholder="Frequency interval" value={form.frequencyInterval} onChange={(e) => setForm({ ...form, frequencyInterval: e.target.value })} />
      <input className="input-field" type="date" value={form.nextDueDate} onChange={(e) => setForm({ ...form, nextDueDate: e.target.value })} />
      <button type="submit" disabled={submitting} className="btn-primary w-full">
        {submitting ? "Creating…" : "Create PM Schedule"}
      </button>
    </form>
  );
}

function PMScheduleEditor({ pm, onChanged }: { pm: PmSchedule; onChanged: () => void }) {
  const [form, setForm] = useState({
    pmName: pm.pm_name,
    frequencyType: pm.frequency_type,
    frequencyInterval: String(pm.frequency_interval),
    nextDueDate: pm.next_due_date ?? "",
    isActive: !!pm.is_active,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [checklist, setChecklist] = useState<PmChecklistItem[]>([]);
  const [checklistLoading, setChecklistLoading] = useState(true);
  const [checklistError, setChecklistError] = useState<string | null>(null);
  const [newItem, setNewItem] = useState({ equipmentGroup: "", checklistItem: "", expectedValue: "" });
  const [addingItem, setAddingItem] = useState(false);
  const [equipmentEdits, setEquipmentEdits] = useState<Record<number, string>>({});
  const [savingItemId, setSavingItemId] = useState<number | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);

  // Equipment choices for this PM come from the Tag No. of assets registered
  // under the same Unit, so the checklist stays consistent with the Asset
  // Register instead of allowing free-text typos.
  useEffect(() => {
    if (!pm.unit_id) return;
    api
      .get<Asset[]>(`/api/assets?unitId=${pm.unit_id}&pageSize=200`)
      .then(setAssets)
      .catch(() => {
        // Non-fatal - the dropdown just won't have options if this fails.
      });
  }, [pm.unit_id]);

  const equipmentOptions = useMemo(() => assets.map((a) => a.tag_no).filter((t): t is string => !!t), [assets]);

  const loadChecklist = async () => {
    setChecklistLoading(true);
    try {
      const items = await api.get<PmChecklistItem[]>(`/api/pm-schedules/${pm.id}/checklist`);
      setChecklist(items);
      const edits: Record<number, string> = {};
      for (const item of items) edits[item.id] = item.equipment_group ?? "";
      setEquipmentEdits(edits);
    } catch (err) {
      setChecklistError(err instanceof Error ? err.message : "Failed to load checklist");
    } finally {
      setChecklistLoading(false);
    }
  };

  useEffect(() => {
    loadChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pm.id]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/pm-schedules/${pm.id}`, {
        pmName: form.pmName,
        frequencyType: form.frequencyType,
        frequencyInterval: Number(form.frequencyInterval),
        nextDueDate: form.nextDueDate || undefined,
        isActive: form.isActive,
      });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save PM schedule");
    } finally {
      setSaving(false);
    }
  };

  const addChecklistItem = async () => {
    if (!newItem.checklistItem.trim()) return;
    setAddingItem(true);
    setChecklistError(null);
    try {
      await api.post(`/api/pm-schedules/${pm.id}/checklist`, {
        checklistItem: newItem.checklistItem.trim(),
        equipmentGroup: newItem.equipmentGroup.trim() || undefined,
        expectedValue: newItem.expectedValue.trim() || undefined,
      });
      setNewItem({ equipmentGroup: newItem.equipmentGroup, checklistItem: "", expectedValue: "" });
      loadChecklist();
    } catch (err) {
      setChecklistError(err instanceof Error ? err.message : "Failed to add checklist item");
    } finally {
      setAddingItem(false);
    }
  };

  const saveEquipmentGroup = async (item: PmChecklistItem, valueOverride?: string) => {
    const newValue = (valueOverride ?? equipmentEdits[item.id] ?? "").trim();
    if (newValue === (item.equipment_group ?? "")) return; // unchanged, skip the call
    setSavingItemId(item.id);
    setChecklistError(null);
    try {
      await api.put(`/api/pm-schedules/${pm.id}/checklist/${item.id}`, {
        checklistItem: item.checklist_item,
        equipmentGroup: newValue || undefined,
        expectedValue: item.expected_value || undefined,
      });
      loadChecklist();
    } catch (err) {
      setChecklistError(err instanceof Error ? err.message : "Failed to update equipment");
    } finally {
      setSavingItemId(null);
    }
  };

  const removeChecklistItem = async (itemId: number) => {
    setChecklistError(null);
    try {
      await api.del(`/api/pm-schedules/${pm.id}/checklist/${itemId}`);
      loadChecklist();
    } catch (err) {
      setChecklistError(err instanceof Error ? err.message : "Failed to remove checklist item");
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={save} className="space-y-3">
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">PM Name</label>
          <input className="input-field" required value={form.pmName} onChange={(e) => setForm({ ...form, pmName: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Frequency</label>
            <select className="input-field" value={form.frequencyType} onChange={(e) => setForm({ ...form, frequencyType: e.target.value })}>
              {FREQUENCIES.map((f) => (
                <option key={f} value={f}>
                  {f.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Every</label>
            <input
              className="input-field"
              type="number"
              min={1}
              value={form.frequencyInterval}
              onChange={(e) => setForm({ ...form, frequencyInterval: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Next Due Date</label>
          <input className="input-field" type="date" value={form.nextDueDate} onChange={(e) => setForm({ ...form, nextDueDate: e.target.value })} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
          Active
        </label>
        <button type="submit" disabled={saving} className="btn-primary w-full">
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </form>

      <div className="border-t border-slate-200 dark:border-industrial-700 pt-4">
        <h3 className="mb-2 text-sm font-semibold">Checklist</h3>
        <p className="mb-3 text-xs text-slate-500">
          These items appear on the Work Order generated from this PM, grouped by Equipment, so a technician can print and carry them (matches a daily checklist sheet layout).
        </p>
        {checklistError && <p className="mb-2 text-xs text-red-600">{checklistError}</p>}
        {checklistLoading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : (
          <ul className="mb-3 space-y-1">
            {checklist.length === 0 && <li className="text-sm text-slate-400">No checklist items yet.</li>}
            {checklist.map((item) => (
              <li
                key={item.id}
                className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 dark:border-industrial-700 px-3 py-2 text-sm"
              >
                <span className="font-mono text-xs text-slate-400">{item.sequence_no}.</span>
                <select
                  className="input-field w-44 py-1 text-xs"
                  value={equipmentEdits[item.id] ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEquipmentEdits((prev) => ({ ...prev, [item.id]: value }));
                    saveEquipmentGroup(item, value);
                  }}
                  disabled={savingItemId === item.id}
                >
                  <option value="">Select equipment (Tag No.)</option>
                  {/* Keep the current value selectable even if it's legacy free text that
                      doesn't match any current asset Tag No., so it isn't silently wiped. */}
                  {equipmentEdits[item.id] && !equipmentOptions.includes(equipmentEdits[item.id]) && (
                    <option value={equipmentEdits[item.id]}>{equipmentEdits[item.id]}</option>
                  )}
                  {equipmentOptions.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
                <span className="flex-1">
                  {item.checklist_item}
                  {item.expected_value && <span className="text-slate-400"> — spec: {item.expected_value}</span>}
                </span>
                <button className="text-xs text-red-600 hover:underline whitespace-nowrap" onClick={() => removeChecklistItem(item.id)}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <select className="input-field" value={newItem.equipmentGroup} onChange={(e) => setNewItem({ ...newItem, equipmentGroup: e.target.value })}>
            <option value="">Select equipment (Tag No.)</option>
            {equipmentOptions.map((tag) => (
              <option key={tag} value={tag}>
                {tag}
              </option>
            ))}
          </select>
          <input
            className="input-field"
            placeholder="Inspection / task"
            value={newItem.checklistItem}
            onChange={(e) => setNewItem({ ...newItem, checklistItem: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addChecklistItem())}
          />
          <input
            className="input-field"
            placeholder="Standard / spec"
            value={newItem.expectedValue}
            onChange={(e) => setNewItem({ ...newItem, expectedValue: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addChecklistItem())}
          />
        </div>
        <button disabled={addingItem || !newItem.checklistItem.trim()} className="btn-secondary mt-2 w-full" onClick={addChecklistItem}>
          {addingItem ? "Adding…" : "Add checklist item"}
        </button>
      </div>
    </div>
  );
}
